import { ShareAltOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { AvatarProps } from 'antd/es/avatar';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { BaseInfoContext } from '@/context/BaseInfoContext';
import { DateTimeFormat } from '@/models/common';
import { TEMP_USER_NAME, useFindUser, User } from '@/models/user';
import { playSound } from '@/utils/sound';
import { getUserAvatar } from '@/utils/uiHelper';

import { createStyles } from 'antd-style';
import UserGender from './Gender';
import type { Props as CompoundNameProps } from './Name';
import UserName from './Name';

export const useStyles = createStyles(({ css }) => ({
  avatar: css`
    .ant-avatar {
      display: block;
    }
  `,
}));

export enum MobileDisplayMode {
  None = 'none',
  Short = 'short',
  Full = 'full',
}

type BaseFieldProps = {};

type AvatarFieldProps = BaseFieldProps & {
  type: 'avatar';
  avatarProps?: AvatarProps;
};

type GenderFieldProps = BaseFieldProps & {
  type: 'gender';
};

type CompoundNameFieldProps = BaseFieldProps & {
  type: 'compoundName';
} & Omit<CompoundNameProps, 'user'>;

type NameFieldProps = BaseFieldProps & {
  type: 'name';
};

type NicknameFieldProps = BaseFieldProps & {
  type: 'nickname';
};

type MobileFieldProps = BaseFieldProps & {
  type: 'mobile';
  mobileDisplayMode?: MobileDisplayMode;
};

type FromUserFieldProps = BaseFieldProps & {
  type: 'fromUser';
};

type ModalTriggerWrapperProps = BaseFieldProps & {
  type: 'modalTriggerWrapper';
  children: React.ReactNode;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
};

type UserFieldProps =
  | AvatarFieldProps
  | GenderFieldProps
  | NameFieldProps
  | CompoundNameFieldProps
  | NicknameFieldProps
  | MobileFieldProps
  | FromUserFieldProps
  | ModalTriggerWrapperProps;

export type UserIntroRenderContext = {
  user: User;

  /**
   * 渲染标准用户字段
   */
  Field: React.FC<UserFieldProps>;
};

export type UserIntroProps = {
  user: User;

  /**
   * 自定义用户信息结构
   */
  children: (context: UserIntroRenderContext) => React.ReactNode;
};

export const UserInfo: React.FC<UserIntroProps> = ({ user: userProp, children }) => {
  const [, , helpers] = useContext(BaseInfoContext);
  const { styles } = useStyles();
  const { sendByUser: fetchUserInfo } = useFindUser();

  const [user, setUser] = useState<User | null>(null);

  const lastRequestedRef = useRef<{
    id?: number;
    username?: string;
  }>({});

  useEffect(() => {
    if (!userProp) {
      setUser(null);
      return;
    }

    if (userProp.addTime) {
      setUser(userProp);
      return;
    }

    if (userProp.id === -1) {
      setUser({
        ...userProp,
        nickname: TEMP_USER_NAME,
        addTime: dayjs().format(DateTimeFormat),
      });
      return;
    }

    const lastRequested = lastRequestedRef.current;

    if (lastRequested.id === userProp.id && lastRequested.username === userProp.username) {
      return;
    }

    lastRequestedRef.current = {
      id: userProp.id,
      username: userProp.username,
    };

    fetchUserInfo(userProp).then(updatedUser => {
      setUser(updatedUser);
    });
  }, [userProp, fetchUserInfo]);

  const showUserModal = useCallback(
    (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e?.stopPropagation();
      playSound('click');
      helpers.showUserModal({
        userId: userProp.id,
      });
    },
    [userProp.id]
  );

  if (!user) {
    return null;
  }

  const Field: React.FC<UserFieldProps> = props => {
    switch (props.type) {
      case 'modalTriggerWrapper':
        const { children, wrapperProps } = props;
        // 单一 child，直接 clone
        if (React.Children.count(children) === 1) {
          const child = React.Children.only(children);
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              onDoubleClick: showUserModal,
            });
          }
        }
        // 多 child，外面包一层 div
        return (
          <div onDoubleClick={showUserModal} {...wrapperProps}>
            {children}
          </div>
        );
      case 'avatar':
        const { avatarProps } = props;
        return (
          <div className={styles.avatar} onDoubleClick={showUserModal}>
            {getUserAvatar(user, avatarProps)}
          </div>
        );

      case 'gender':
        return <UserGender gender={user.gender} />;

      case 'compoundName':
        const { spaceProps = {} } = props;
        return (
          <UserName
            user={user}
            {...props}
            spaceProps={{
              ...spaceProps,
              onDoubleClick: showUserModal,
            }}
          />
        );

      case 'name':
        return (
          <span
            onDoubleClick={showUserModal}
            style={{
              fontWeight: 500,
              wordBreak: 'break-all',
            }}>
            {user.remarkName || user.nickname || '匿名客户'}
          </span>
        );

      case 'nickname':
        return user.nickname ? <span style={{ color: '#999' }}>({user.nickname})</span> : null;

      case 'mobile':
        const { mobileDisplayMode } = props;
        if (!user.mobile || mobileDisplayMode === MobileDisplayMode.None) {
          return null;
        }

        return (
          <span style={{ color: '#999' }}>
            [{mobileDisplayMode === MobileDisplayMode.Short ? user.mobile.slice(-4) : user.mobile}]
          </span>
        );

      case 'fromUser':
        if (!user.fromUserId || user.fromUserId <= 0) {
          return null;
        }

        return (
          <Tag
            icon={<ShareAltOutlined />}
            style={{ userSelect: 'none' }}
            onDoubleClick={e => {
              e.stopPropagation();
              helpers.showUserModal({ userId: user.fromUserId });
            }}>
            {user.fromUserId}
          </Tag>
        );

      default:
        return null;
    }
  };

  return children({
    user,
    Field,
  });
};
